// M2 – Popup thanh toán (chế độ bán hàng và thu nợ)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { Banknote, Check, CreditCard, Landmark, Loader2, Smartphone, Wallet, X } from 'lucide-react';
import { METHOD_LABEL, METHODS, METHODS_NEED_REF, type Customer, type PaymentLineInput, type PaymentMethod } from '../data';
import usePosStore from '../store';
import { money } from '../utils';
import MoneyInput from './MoneyInput';
import VietQR from './VietQR';

const METHOD_ICON: Record<PaymentMethod, React.ReactNode> = {
  cash: <Banknote className="h-4 w-4" />,
  transfer: <Landmark className="h-4 w-4" />,
  card: <CreditCard className="h-4 w-4" />,
  ewallet: <Smartphone className="h-4 w-4" />,
  prepaid: <Wallet className="h-4 w-4" />,
};

interface Props {
  open: boolean;
  mode: 'sale' | 'collect';
  amountDue: number;
  orderCode: string;
  customer: Customer | null;
  onClose: () => void;
  /** Trả về Promise để popup giữ trạng thái "Đang xác nhận…" và chặn bấm lặp */
  onConfirm: (payments: PaymentLineInput[], isDebt: boolean) => Promise<void> | void;
  /** Giới hạn phương thức được chọn (mặc định: tất cả) */
  methods?: PaymentMethod[];
  /** Nội dung chuyển khoản VietQR (mặc định: mã đơn) */
  qrContent?: string;
  /** Dòng ghi chú dưới tiêu đề */
  hint?: string;
  /** Số tiền điền sẵn (vd. đợt trả góp đến hạn) – mặc định thu đủ amountDue */
  suggestedAmount?: number;
  /** false: phải thu đủ, không có "Ghi nợ phần còn thiếu" (bán lẻ sản phẩm, đợt 1 trả góp) */
  allowDebt?: boolean;
  /** Khi allowDebt=false: dòng nhắc ở chân popup và lý do chặn khi thu thiếu */
  fullPayNote?: { hint: string; block: string };
  /** Thu nợ: dòng nhắc ở chân popup theo số đang nhập (vd. trả góp nói rõ thu đợt nào); undefined = câu mặc định */
  collectNote?: (entered: number, remaining: number) => string | undefined;
}

const RETAIL_FULL_PAY = { hint: 'Bán lẻ thanh toán một lần – thu đủ số tiền', block: 'Còn thiếu tiền – bán lẻ phải thu đủ' };

export default function PaymentModal({
  open, mode, amountDue, orderCode, customer, onClose, onConfirm, methods = METHODS, qrContent, hint, suggestedAmount, allowDebt = true,
  fullPayNote = RETAIL_FULL_PAY, collectNote,
}: Props) {
  const config = usePosStore(s => s.config);
  const [lines, setLines] = useState<PaymentLineInput[]>([]);
  const [isDebt, setIsDebt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  // Số tiền gốc khi đổi tab phương thức: số điền sẵn hoặc số người dùng gõ – không phải số đã bị cắt theo số dư ví
  const [baseAmount, setBaseAmount] = useState(0);
  const amountRefs = useRef<(HTMLInputElement | null)[]>([]);

  const initialAmount = suggestedAmount && suggestedAmount > 0 ? Math.min(suggestedAmount, amountDue) : amountDue;
  useEffect(() => {
    if (open) {
      setLines([{ method: 'cash', amount: initialAmount, tendered: initialAmount, txnRef: '' }]);
      setBaseAmount(initialAmount);
      setIsDebt(false);
      setSubmitting(false);
      setTouched(false);
    }
  }, [open, initialAmount]);
  // Một dòng: các nút phương thức là tab, bấm là đổi phương thức của dòng đó.
  // Chỉ khi bấm "Chia thêm phương thức" mới có nhiều dòng (vd. một phần ví trả trước + tiền mặt).
  const replaceable = lines.length === 1;

  const entered = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const remaining = Math.max(0, amountDue - entered);
  const change = useMemo(() => {
    let excess = Math.max(0, entered - amountDue);
    let total = 0;
    lines.forEach(l => {
      if (l.method !== 'cash') return;
      const cut = Math.min(excess, l.amount);
      excess -= cut;
      total += Math.max(0, l.tendered - (l.amount - cut));
    });
    return total;
  }, [lines, entered, amountDue]);

  const prepaidBalance = customer?.prepaid || 0;
  const debtAllowed = mode === 'sale' && allowDebt && config.allowDebt && !!customer && remaining > 0;
  useEffect(() => { if (!debtAllowed) setIsDebt(false); }, [debtAllowed]);

  const methodDisabledReason = (m: PaymentMethod): string | null => {
    if (m === 'prepaid' && !customer) return 'Cần chọn khách hàng';
    if (m === 'prepaid' && prepaidBalance <= 0) return 'Ví trả trước không có số dư';
    if (replaceable) return m === lines[0].method ? 'Đang chọn phương thức này' : null;
    if (lines.length >= config.maxPaymentLines) return `Tối đa ${config.maxPaymentLines} dòng thanh toán`;
    if (m !== 'transfer' && lines.some(l => l.method === m)) return 'Đã có dòng này';
    if (remaining <= 0 && m !== 'cash') return 'Đã đủ tiền';
    return null;
  };

  const addLine = (m: PaymentMethod) => {
    if (methodDisabledReason(m)) return;
    if (replaceable) {
      // Giữ số tiền đang nhập, chỉ đổi phương thức (ngoài tiền mặt không được vượt số cần thu).
      // Lấy từ baseAmount: chọn nhầm ví trả trước (bị cắt theo số dư) rồi đổi lại thì về đúng số cũ
      const amount = m === 'prepaid' ? Math.min(prepaidBalance, baseAmount, amountDue) : m === 'cash' ? baseAmount : Math.min(baseAmount, amountDue);
      setLines([{ method: m, amount, tendered: amount, txnRef: '' }]);
      setTimeout(() => amountRefs.current[0]?.focus(), 50);
      return;
    }
    const amount = m === 'prepaid' ? Math.min(prepaidBalance, remaining) : remaining;
    setLines(prev => [...prev, { method: m, amount, tendered: amount, txnRef: '' }]);
    setTimeout(() => amountRefs.current[lines.length]?.focus(), 50);
  };

  // Phương thức còn thêm được thành dòng mới khi khách trả bằng nhiều cách – thu ngân tự chọn
  const splitMethods = lines.length < config.maxPaymentLines
    ? methods.filter(m => !lines.some(l => l.method === m) && !(m === 'prepaid' && (!customer || prepaidBalance <= 0)))
    : [];
  const addSplitLine = (m: PaymentMethod) => {
    const amount = m === 'prepaid' ? Math.min(prepaidBalance, remaining) : remaining;
    setLines(prev => [...prev, { method: m, amount, tendered: amount, txnRef: '' }]);
    setTimeout(() => amountRefs.current[lines.length]?.focus(), 50);
  };

  const updateLine = (i: number, patch: Partial<PaymentLineInput>) => {
    if (patch.amount !== undefined && lines.length === 1) setBaseAmount(patch.amount);
    setLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      const next = { ...l, ...patch };
      // BR2.1: chỉ tiền mặt được nhập vượt phần còn thiếu
      if (patch.amount !== undefined && next.method !== 'cash') {
        const others = prev.reduce((s, x, j) => (j === i ? s : s + x.amount), 0);
        next.amount = Math.min(next.amount, Math.max(0, amountDue - others));
        if (next.method === 'prepaid') next.amount = Math.min(next.amount, prepaidBalance);
      }
      // "Khách đưa" đang bằng số tiền (chưa sửa tay) thì chạy theo số tiền; không bao giờ nhỏ hơn số tiền
      if (next.method === 'cash' && patch.amount !== undefined && (l.tendered === l.amount || next.tendered < next.amount)) next.tendered = next.amount;
      return next;
    }));
  };

  const lineError = (l: PaymentLineInput): string | null => {
    if (l.amount <= 0) return 'Số tiền phải lớn hơn 0';
    if (METHODS_NEED_REF.includes(l.method)) {
      const ref = l.txnRef.trim();
      if (!ref) return 'Bắt buộc nhập mã giao dịch';
      if (!/^[A-Za-z0-9]{4,50}$/.test(ref)) return 'Mã giao dịch 4–50 ký tự chữ và số';
    }
    if (l.method === 'cash' && l.tendered < l.amount) return 'Khách đưa chưa đủ số tiền dòng này';
    if (l.method === 'prepaid' && l.amount > prepaidBalance) return 'Vượt số dư ví trả trước';
    return null;
  };
  const errors = lines.map(lineError);
  const hasError = errors.some(Boolean);
  const blockReason =
    lines.length === 0 ? 'Chọn ít nhất 1 phương thức' :
    hasError ? 'Kiểm tra lại các dòng thanh toán' :
    mode === 'sale' && remaining > 0 && !isDebt ? (allowDebt ? 'Còn thiếu tiền – thu đủ hoặc chọn Ghi nợ' : fullPayNote.block) : null;

  // Khóa ngay từ cú bấm đầu: state `submitting` chỉ cập nhật sau render nên bấm đúp vẫn lọt 2 lượt gửi
  const busy = useRef(false);
  const confirm = async () => {
    setTouched(true);
    if (blockReason || busy.current) return;
    busy.current = true;
    setSubmitting(true);
    try {
      await onConfirm(lines.filter(l => l.amount > 0), isDebt);
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => !submitting && onClose()}
      footer={null}
      width={780}
      // Ghim phía trên (không canh giữa) để thêm / xóa dòng không làm các nút nhảy chỗ dưới tay
      style={{ top: 24 }}
      destroyOnHidden
      maskClosable={false}
      closable={false}
      styles={{ content: { padding: 0, overflow: 'hidden' } }}
    >
      <div className="flex max-h-[90vh] flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{mode === 'sale' ? 'Thanh toán đơn' : 'Thu nợ'}</h2>
            <div className="text-xs text-slate-500">{orderCode}{customer ? ` · ${customer.name}` : ' · Khách lẻ'}</div>
            {hint && <div className="mt-0.5 text-xs text-amber-700">{hint}</div>}
          </div>
          <button onClick={onClose} disabled={submitting} className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Đóng">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className="flex items-center justify-between rounded-xl border border-[#3e91ab]/30 bg-[#ecf6f9] px-4 py-3">
            <span className="text-base font-medium text-slate-600">{mode === 'sale' ? 'Cần thu' : 'Còn nợ'}</span>
            <span className="text-3xl font-extrabold text-[#2c6b7f]">{money(amountDue)}đ</span>
          </div>

          <div className={`grid grid-cols-2 gap-2 ${methods.length >= 5 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
            {methods.map(m => {
              const reason = methodDisabledReason(m);
              const selected = lines.some(l => l.method === m);
              return (
                <button
                  key={m}
                  onClick={() => addLine(m)}
                  disabled={!!reason}
                  title={reason || (replaceable ? `Thanh toán bằng ${METHOD_LABEL[m]}` : undefined)}
                  aria-pressed={selected}
                  className={`flex h-12 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition ${
                    selected ? 'border-[#357f96] bg-[#ecf6f9] text-[#2c6b7f] ring-1 ring-[#357f96]'
                      : reason ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-[#3e91ab] hover:bg-[#ecf6f9]'
                  }`}
                >
                  {METHOD_ICON[m]} {METHOD_LABEL[m]}
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {lines.map((l, i) => {
              // Lỗi "bắt buộc mã GD" / "số tiền 0" (vừa chia dòng khi đã đủ tiền) chỉ hiện sau khi bấm xác nhận để không báo đỏ ngay khi vừa thêm dòng
              const err = touched || !(errors[i]?.startsWith('Bắt buộc') || l.amount <= 0) ? errors[i] : null;
              return (
                <div key={i} className={`relative rounded-xl border p-4 ${err ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
                  {lines.length > 1 && (
                    <button onClick={() => {
                      const rest = lines.filter((_, j) => j !== i);
                      setLines(rest);
                      if (rest.length === 1) setBaseAmount(rest[0].amount);
                    }} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-red-500" aria-label="Xóa dòng">
                      <X className="h-5 w-5" />
                    </button>
                  )}
                  <div className="mb-3 flex items-center gap-2 pr-10 text-sm font-bold text-slate-700">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs text-slate-500 ring-1 ring-slate-200">{i + 1}</span>
                    {METHOD_ICON[l.method]} {METHOD_LABEL[l.method]}
                  </div>
                  {/* Chuyển khoản: mã QR lên trước – khách quét xong mới có mã giao dịch để nhập */}
                  {l.method === 'transfer' && <VietQR amount={l.amount} content={qrContent || orderCode} className="mb-3" />}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-slate-500">Số tiền</span>
                      <MoneyInput ref={el => { amountRefs.current[i] = el; }} value={l.amount} onChange={v => updateLine(i, { amount: v })} invalid={l.amount <= 0} />
                    </label>
                    {l.method === 'cash' && (
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-slate-500">Khách đưa</span>
                        <MoneyInput value={l.tendered} onChange={v => updateLine(i, { tendered: v })} invalid={l.tendered < l.amount} />
                      </label>
                    )}
                    {METHODS_NEED_REF.includes(l.method) && (
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-slate-500">Mã giao dịch <span className="text-red-500">*</span></span>
                        <input
                          value={l.txnRef}
                          onChange={e => updateLine(i, { txnRef: e.target.value.toUpperCase().replace(/\s/g, '') })}
                          placeholder="VD: FT26100712345"
                          maxLength={50}
                          className={`h-11 w-full rounded-lg border bg-white px-3 text-base uppercase outline-none focus:ring-2 ${errors[i] && (touched || l.txnRef) ? 'border-red-400 focus:ring-red-200' : 'border-slate-300 focus:border-[#3e91ab] focus:ring-[#3e91ab]/20'}`}
                        />
                      </label>
                    )}
                    {l.method === 'prepaid' && (
                      <div className="flex items-end pb-2 text-sm text-slate-500">
                        Số dư khả dụng:&nbsp;<b className="text-slate-700">{money(prepaidBalance)}đ</b>
                      </div>
                    )}
                  </div>
                  {l.method === 'cash' && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[500_000, 1_000_000, 2_000_000].map(v => (
                        <button key={v} onClick={() => updateLine(i, { tendered: l.tendered + v })} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                          +{v >= 1_000_000 ? `${v / 1_000_000}tr` : `${v / 1000}k`}
                        </button>
                      ))}
                      <button onClick={() => updateLine(i, { tendered: l.amount })} className="h-10 rounded-lg border border-[#3e91ab]/40 bg-[#ecf6f9] px-3 text-sm font-semibold text-[#2c6b7f] hover:bg-[#d9eef4]">Đủ</button>
                      {l.tendered > l.amount && <span className="flex items-center text-sm text-slate-500">Thối dòng này: <b className="ml-1 text-slate-800">{money(l.tendered - l.amount)}</b></span>}
                    </div>
                  )}
                  {err && <p className="mt-2 text-sm font-medium text-red-600">{err}</p>}
                </div>
              );
            })}
            {splitMethods.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-slate-500">+ Chia thêm:</span>
                {splitMethods.map(m => (
                  <button key={m} onClick={() => addSplitLine(m)}
                    className="flex h-9 items-center gap-1.5 rounded-lg border border-dashed border-[#357f96]/50 bg-white px-3 font-semibold text-[#2c6b7f] hover:bg-[#ecf6f9]">
                    {METHOD_ICON[m]} {METHOD_LABEL[m]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <div className="mb-3 grid grid-cols-3 gap-2">
            <div><div className="text-xs text-slate-500">Đã nhập</div><div className="text-lg font-bold text-slate-800">{money(entered)}</div></div>
            {/* Thu nợ: phần chưa thu là nợ còn lại (có thể theo lịch trả góp), không phải "thiếu" như khi bán */}
            <div className="text-center">
              <div className="text-xs text-slate-500">{mode === 'sale' ? 'Còn thiếu' : 'Còn nợ sau thu'}</div>
              <div className={`text-lg font-bold ${remaining <= 0 ? 'text-slate-800' : mode === 'sale' ? 'text-red-600' : 'text-amber-700'}`}>{money(remaining)}</div>
            </div>
            <div className="text-right"><div className="text-xs text-slate-500">Tiền thối</div><div className={`text-2xl font-extrabold ${change > 0 ? 'text-green-600' : 'text-slate-800'}`}>{money(change)}</div></div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {mode === 'sale' && !allowDebt ? (
              <span className="max-w-xs text-sm text-slate-500">{fullPayNote.hint}</span>
            ) : mode === 'sale' ? (
              <label className={`flex items-center gap-2 text-sm font-medium ${debtAllowed ? 'cursor-pointer text-slate-700' : 'cursor-not-allowed text-slate-400'}`}>
                <input type="checkbox" checked={isDebt} disabled={!debtAllowed} onChange={e => setIsDebt(e.target.checked)} className="h-5 w-5 rounded border-slate-300 accent-[#357f96]" />
                Ghi nợ phần còn thiếu
                {!customer && remaining > 0 && <span className="text-xs font-normal text-amber-600">(cần chọn khách hàng)</span>}
                {!config.allowDebt && <span className="text-xs font-normal text-slate-400">(đang tắt ghi nợ)</span>}
              </label>
            ) : (
              <span className="max-w-xs text-sm text-slate-500">
                {collectNote?.(entered, remaining) ?? (remaining > 0 ? `Thu một phần – còn nợ ${money(remaining)}đ sau khi thu` : 'Thu đủ – đơn chuyển Hoàn thành')}
              </span>
            )}
            <div className="flex gap-2">
              <button onClick={onClose} disabled={submitting} className="h-12 flex-1 rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100 sm:flex-none">Quay lại</button>
              <button
                onClick={confirm}
                disabled={submitting || (touched && !!blockReason)}
                title={blockReason || undefined}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-[#357f96] px-6 text-sm font-bold text-white shadow-md hover:bg-[#2c6b7f] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
              >
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Đang xác nhận…</> : <><Check className="h-5 w-5" /> {mode === 'sale' ? 'XÁC NHẬN THANH TOÁN' : 'XÁC NHẬN THU NỢ'}</>}
              </button>
            </div>
          </div>
          {touched && blockReason && <p className="mt-2 text-right text-sm font-medium text-red-600">{blockReason}</p>}
        </div>
      </div>
    </Modal>
  );
}
